import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import DNErrorViewer from './DNErrorViewer';
import DNOverview from './DNOverview';
import DNProductDetail from './DNProductDetail';
import DNProductsTable from './DNProductsTable';
import DNControl from './DNControl';
import { useDN } from '../../contexts/DNContext';
import useAnalytics from '../../hooks/useAnalytics';
import DNMap from './DNMap';
import { FaMapMarker, FaBuilding } from 'react-icons/fa';
import DNInterventionPoint from './DNInterventionPoint';
import { _t } from '../../utils/i18n';

// Mémoiser les tabs pour éviter les re-créations inutiles
const DNTabs = React.memo(({
  selectedProject,
  fetchWithAuth,
  selectedShopSigns,
  selectedCategories,
  currentUser,
  projectConfig,
  projectConfigLoaded,
  obfuscate
}) => {
  const projectId = selectedProject.value;
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'dn_general');
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    setSelectedProduct(null);
  }, [selectedProject]);

  const { products, issues, startDate } = useDN();
  const newIssues = issues.filter(issue => new Date(issue.date) >= new Date(startDate));

  const { track } = useAnalytics({ currentUser, project: selectedProject.label });

  // Effet pour initialiser le tab dans l'URL au premier chargement
  useEffect(() => {
    if (!searchParams.has('tab')) {
      // Préserver tous les paramètres existants
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('tab', activeTab);

      // Vérifier que nous ne perdons pas le projectId
      if (!newSearchParams.has('projectId') && projectId) {
        newSearchParams.set('projectId', projectId);
      }

      setSearchParams(newSearchParams, { replace: true });
    }

    track('dn_view_tab', {
      view_tab: activeTab,
      project: selectedProject.label
    });

  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (tabId) => {
    // Préserver tous les paramètres existants
    const newSearchParams = new URLSearchParams(searchParams);

    if (tabId) {
      newSearchParams.set('tab', tabId);
    } else {
      newSearchParams.delete('tab');
    }

    // Vérifier que nous ne perdons pas le projectId
    if (!newSearchParams.has('projectId') && projectId) {
      newSearchParams.set('projectId', projectId);
    }

    setSearchParams(newSearchParams);
    setActiveTab(tabId);
  };

  const handleProductClick = (productId) => {
    setSelectedProduct(productId);
    setTimeout(() => {
      setActiveTab('dn_products');
    }, 100);
  };

  const tabs = [
    {
      id: 'dn_general',
      label: _t('General'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"></path></svg>
      ),
      content: (
        <DNOverview
          currentUser={currentUser}
          projectId={projectId}
          selectedShopSigns={selectedShopSigns}
          selectedCategories={selectedCategories}
          projectConfig={projectConfig}
          projectConfigLoaded={projectConfigLoaded}
          fetchWithAuth={fetchWithAuth}
          handleTabChange={handleTabChange}
        />
      )
    },
    {
      id: 'dn_list',
      label: _t('Product detail'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5 4a3 3 0 00-3 3v6a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H5zm-1 9a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zm1-3a1 1 0 100 2h10a1 1 0 100-2H5zm-1-3a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1z" clipRule="evenodd" />
        </svg>
      ),
      content: (
        <DNProductsTable
          products={products}
          selectedShopSigns={selectedShopSigns}
          selectedCategories={selectedCategories}
          onProductClick={handleProductClick}
          obfuscate={obfuscate}
        />
      )
    },
    {
      id: 'dn_products',
      label: _t('Product trend'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 0l-2 2a1 1 0 101.414 1.414L8 10.414l1.293 1.293a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      ),
      content: (
        <DNProductDetail
          currentUser={currentUser}
          selectedProject={selectedProject}
          fetchWithAuth={fetchWithAuth}
          selectedShopSigns={selectedShopSigns}
          selectedCategories={selectedCategories}
          defaultProduct={selectedProduct}
          obfuscate={obfuscate}
        />
      )
    },
    {
      id: 'dn_focus_magasin',
      label: _t('Store focus'),
      icon: <FaBuilding />,
      content: (
        <DNInterventionPoint
          selectedProject={selectedProject}
          fetchWithAuth={fetchWithAuth}
          selectedShopSigns={[]}
          selectedCategories={selectedCategories}
          obfuscate={obfuscate}
        />
      )
    },
    {
      id: 'dn_errors',
      label: _t('Absences') + ` (${newIssues.length})`,
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
        </svg>
      ),
      content: (
        <DNErrorViewer
          currentUser={currentUser}
          fetchWithAuth={fetchWithAuth}
          projectId={projectId}
          selectedShopSigns={selectedShopSigns}
          selectedCategories={selectedCategories}
          obfuscate={obfuscate}
        />
      )
    },
    {
      id: 'dn_map',
      label: _t('Map'),
      icon: <FaMapMarker />,
      content: (
        <DNMap
          currentUser={currentUser}
          selectedProject={selectedProject}
          fetchWithAuth={fetchWithAuth}
          selectedShopSigns={selectedShopSigns}
          selectedCategories={selectedCategories}
          obfuscate={obfuscate}
        />
      )
    },
    {
      id: 'dn_control',
      label: _t('Admin'),
      admin: true,
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
        </svg>
      ),
      content: (
        <DNControl
          selectedProject={selectedProject}
          fetchWithAuth={fetchWithAuth}
          currentUser={currentUser}
          selectedShopSigns={selectedShopSigns}
          selectedCategories={selectedCategories}
        />
      )
    },
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
      {/* Tabs Navigation - Avec scrolling horizontal uniquement sur mobile */}
      <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto overflow-y-hidden">
        <nav className="flex -mb-px min-w-max" aria-label="Tabs">
          {tabs.map((tab) => {
            if (tab.admin && !currentUser.isadmin && !currentUser.issuperadmin)
              return null;

            return <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`
                flex items-center px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap
                ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }
              `}
            >
              <span className="hidden sm:block">{tab.icon}</span>
              <span className="sm:ml-2">{tab.label}</span>
              {tab.admin && (
                <span className="ml-1 inline-flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-purple-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2L4 6v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V6l-8-4z"/>
                    <text x="13" y="16" textAnchor="middle" className="text-xs font-bold fill-white">A</text>
                  </svg>
                </span>
              )}
            </button>
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="sm:p-4">
        {tabs.find(tab => tab.id === activeTab)?.content || tabs[0]?.content}
      </div>
    </div>
  );
});

export default DNTabs;